// THREEJS_SHOWCASE_MARKER — 3D Showcase page.
// Five real WebGL (Three.js / react-three-fiber) demos, each in its own card
// with its OWN <Canvas>, lazy-loaded so one heavy canvas never blocks the rest.
// Structured render path (bespoke React), dark aesthetic to match the dashboard.
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import type { ComponentType } from "react";
import { Boxes, Globe2, Droplet, Gem, Rocket, Loader2 } from "lucide-react";

const ParticleMorphField = lazy(() => import("./ParticleMorphField"));
const DataGlobe = lazy(() => import("./DataGlobe"));
const IridescentBlob = lazy(() => import("./IridescentBlob"));
const HeroObject = lazy(() => import("./HeroObject"));
const WarpStarfield = lazy(() => import("./WarpStarfield"));

type Demo = {
  id: string;
  title: string;
  caption: string;
  technique: string;
  icon: typeof Boxes;
  accent: string;
  Component: ComponentType;
  hint: string;
};

const DEMOS: Demo[] = [
  {
    id: "particles",
    title: "Particle Morph Field",
    caption: "120,000 GPU points morphing between a sphere and a galaxy spiral.",
    technique:
      "BufferGeometry points + additive blending + Bloom. Use-case: animated hero backdrops and data clouds that feel alive without a video file.",
    icon: Boxes,
    accent: "text-orange-400 bg-orange-500/15",
    Component: ParticleMorphField,
    hint: "Move your mouse · auto-morphs sphere ↔ galaxy",
  },
  {
    id: "globe",
    title: "Data Globe",
    caption: "A dot-globe with glowing network arcs rising between world cities.",
    technique:
      "Tube-geometry great-circle arcs + Bloom + OrbitControls. Use-case: global traffic, customer maps, logistics — the classic 'reach' visual, done in real 3D.",
    icon: Globe2,
    accent: "text-teal-400 bg-teal-500/15",
    Component: DataGlobe,
    hint: "Drag to spin · scroll to zoom",
  },
  {
    id: "blob",
    title: "Iridescent Liquid Blob",
    caption: "An organic, slowly-morphing mesh with real reflections and iridescence.",
    technique:
      "drei MeshDistortMaterial + Lightformer Environment for true env-map reflections. Use-case: premium product/brand moments and loading states.",
    icon: Droplet,
    accent: "text-violet-400 bg-violet-500/15",
    Component: IridescentBlob,
    hint: "Drag to orbit",
  },
  {
    id: "hero",
    title: "Orbit Hero Object",
    caption: "A polished crystal torus-knot under studio lighting with contact shadows.",
    technique:
      "MeshPhysicalMaterial (transmission + clearcoat) + ContactShadows + AutoRotate. Use-case: a 3D hero/product showpiece you can spin — no external model needed.",
    icon: Gem,
    accent: "text-fuchsia-400 bg-fuchsia-500/15",
    Component: HeroObject,
    hint: "Auto-rotating · drag to take over",
  },
  {
    id: "warp",
    title: "Warp Starfield",
    caption: "A cinematic fly-through of star streaks rushing past the camera.",
    technique:
      "Instanced mesh streaks recycled in a loop + fog + Bloom + mouse parallax. Use-case: transitions, launch/'liftoff' moments, attention-grabbing intros.",
    icon: Rocket,
    accent: "text-sky-400 bg-sky-500/15",
    Component: WarpStarfield,
    hint: "Move your mouse for parallax",
  },
];

function CanvasFallback() {
  return (
    <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
      <Loader2 className="h-6 w-6 animate-spin" />
      <span className="ml-2 text-xs">Loading WebGL…</span>
    </div>
  );
}

// Mounts the heavy canvas only once the card scrolls into view, so five GPU
// contexts don't all spin up at page load.
function DemoCard({ demo }: { demo: Demo }) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const Icon = demo.icon;
  const Component = demo.Component;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="overflow-hidden rounded-2xl border border-white/10 bg-[#0a0b14] shadow-xl"
    >
      <div className="flex items-start gap-3 border-b border-white/10 px-5 py-4">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${demo.accent}`}>
          <Icon className="h-[18px] w-[18px]" />
        </div>
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-white">{demo.title}</h2>
          <p className="text-sm text-white/60">{demo.caption}</p>
        </div>
      </div>

      <div className="relative h-[380px] w-full bg-black">
        {inView ? (
          <Suspense fallback={<CanvasFallback />}>
            <Component />
          </Suspense>
        ) : (
          <CanvasFallback />
        )}
        <div className="pointer-events-none absolute bottom-3 left-3 rounded-md bg-black/50 px-2.5 py-1 text-[11px] font-medium text-white/70 backdrop-blur">
          {demo.hint}
        </div>
      </div>

      <div className="border-t border-white/10 px-5 py-3">
        <p className="text-xs leading-relaxed text-white/50">{demo.technique}</p>
      </div>
    </div>
  );
}

export function ThreeShowcasePage() {
  return (
    <div className="max-w-6xl px-6 pb-16 pt-4">
      <div className="mb-6">
        <div className="mb-1 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/15">
            <Boxes className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">3D Showcase</h1>
            <p className="text-sm text-muted-foreground">
              Five real WebGL demos built with Three.js &amp; react-three-fiber — depth, lighting,
              reflections, post-processing glow, and mouse interaction. Each runs in its own
              lazy-loaded canvas.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {DEMOS.map((demo) => (
          <DemoCard key={demo.id} demo={demo} />
        ))}
      </div>
    </div>
  );
}

export default ThreeShowcasePage;
